/**
 * Shared formatting utilities for currency, dates, ordinals, and relative time.
 */

export function formatCurrency(
  amount: number | string | undefined | null,
  decimals = 2,
): string {
  if (amount == null) return '$0.00';
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '$0.00';
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Parse a date input as a LOCAL-timezone Date so `YYYY-MM-DD` strings
 * don't drift by a day. `new Date("2026-05-07")` parses as UTC midnight,
 * which renders as May 6 in any UTC-negative locale (PT/MT/CT/ET). For
 * date-only inputs we want the wall-clock day, regardless of timezone.
 * Falls back to the native parser for anything with a time component
 * (ISO timestamps, RFC strings) so those still respect timezone.
 */
function parseLocalDate(input: string | Date | null | undefined): Date | null {
  if (input == null) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  // Bare YYYY-MM-DD → local midnight, no UTC drift.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
  if (m) {
    const [, y, mo, d] = m;
    return new Date(Number(y), Number(mo) - 1, Number(d));
  }
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

export function formatDate(
  date: string | undefined | null,
  format: 'short' | 'long' = 'short',
): string {
  const d = parseLocalDate(date);
  if (!d) return '';
  return d.toLocaleDateString('en-US', {
    month: format === 'short' ? 'short' : 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatDateRange(
  start: string | undefined | null,
  end?: string | null,
): string {
  const s = parseLocalDate(start);
  if (!s) return '';

  const e = parseLocalDate(end);
  if (!e) return formatDate(start);

  const sameYear = s.getFullYear() === e.getFullYear();
  const sameMonth = sameYear && s.getMonth() === e.getMonth();
  const sameDay = sameMonth && s.getDate() === e.getDate();

  if (sameDay) return formatDate(start);

  if (sameMonth) {
    const month = s.toLocaleDateString('en-US', { month: 'short' });
    return `${month} ${s.getDate()}-${e.getDate()}, ${s.getFullYear()}`;
  }

  if (sameYear) {
    const sMonth = s.toLocaleDateString('en-US', { month: 'short' });
    const eMonth = e.toLocaleDateString('en-US', { month: 'short' });
    return `${sMonth} ${s.getDate()} - ${eMonth} ${e.getDate()}, ${s.getFullYear()}`;
  }

  return `${formatDate(start)} - ${formatDate(end)}`;
}

/**
 * Format a Postgres `time` column (HH:MM:SS[±tz]) as "h:mm AM/PM".
 * Returns an empty string when the input is falsy/invalid so callers can
 * render inside a truthy-guarded block without noise.
 */
export function formatTime(time: string | number | null | undefined): string {
  if (!time) return '';
  const str = String(time);
  const match = /^(\d{1,2}):(\d{2})/.exec(str);
  if (!match) return '';
  const hours24 = Number(match[1]);
  const minutes = match[2];
  if (Number.isNaN(hours24) || hours24 < 0 || hours24 > 23) return '';
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${minutes} ${period}`;
}

export function getOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function getRelativeTime(date: string | undefined | null): string {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const absDiff = Math.abs(diffMs);
  const isFuture = diffMs > 0;

  const minutes = Math.floor(absDiff / 60000);
  const hours = Math.floor(absDiff / 3600000);
  const days = Math.floor(absDiff / 86400000);

  let text: string;
  if (minutes < 1) text = 'just now';
  else if (minutes < 60) text = `${minutes} minute${minutes === 1 ? '' : 's'}`;
  else if (hours < 24) text = `${hours} hour${hours === 1 ? '' : 's'}`;
  else if (days < 30) text = `${days} day${days === 1 ? '' : 's'}`;
  else {
    const months = Math.floor(days / 30);
    text = `${months} month${months === 1 ? '' : 's'}`;
  }

  if (text === 'just now') return text;
  return isFuture ? `${text} from now` : `${text} ago`;
}
